/**
 * User-role super properties attached to EVERY tracked event.
 *
 * This mirrors the contract already shipped in the mobile app so segmentation
 * data is identical across platforms. The three flags are:
 *   - non-exclusive (a user can be several roles at once),
 *   - always present on every event (default `false`),
 *   - real booleans (never strings/numbers/null).
 *
 * A "plain user" is simply all three `false` — there is no separate `is_user`.
 *
 * The values are derived on the client from the app's existing role lookups
 * (super-admin check + managed comedians/venues) and persisted here so the
 * non-React `trackEvent` wrapper can read them synchronously for each event.
 * Written by `UserRolesProvider` (apps/web) and cleared on logout via
 * `resetAnalytics`.
 */
export interface UserRoleProperties {
  /** User is a super admin. */
  is_super_admin: boolean;
  /** User manages at least one venue. */
  is_venue_manager: boolean;
  /** User manages at least one comedian. */
  is_comedian_manager: boolean;
}

const STORAGE_KEY = "user_roles";

/** The all-false baseline — a plain/anonymous user. */
export const DEFAULT_USER_ROLES: UserRoleProperties = {
  is_super_admin: false,
  is_venue_manager: false,
  is_comedian_manager: false,
};

/** Persist the current user's role flags for later attachment to events. */
export function storeUserRoles(roles: UserRoleProperties): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  } catch {
    // Ignore write failures (storage full / disabled).
  }
}

/** Remove any persisted role flags (called on logout). */
export function clearStoredUserRoles(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore removal failures.
  }
}

/**
 * Read the persisted role flags for attachment to an event.
 *
 * Always returns all three keys as real booleans. When nothing is stored
 * (anonymous user, cold load before roles resolve, or storage disabled) every
 * key defaults to `false`, so each event still carries the full contract.
 */
export function getStoredUserRoles(): UserRoleProperties {
  if (typeof window === "undefined") return { ...DEFAULT_USER_ROLES };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_USER_ROLES };
    const parsed = JSON.parse(raw) as Partial<UserRoleProperties> | null;
    return {
      is_super_admin: parsed?.is_super_admin === true,
      is_venue_manager: parsed?.is_venue_manager === true,
      is_comedian_manager: parsed?.is_comedian_manager === true,
    };
  } catch {
    return { ...DEFAULT_USER_ROLES };
  }
}
